import type { QueryClient, QueryKey } from '@tanstack/react-query'

/**
 * Query keys, by domain.
 *
 * Every key starts with its domain (`devices`, `brands`, `locations`, …) so that a mutation can
 * invalidate the whole domain by prefix and reach every list that shows it — the devices table,
 * the map, the pickers — without knowing which pages exist. The failure this replaces: the brands
 * administration page invalidated `['admin-brands']` while nine other pages read `['brands']`, so a
 * renamed brand kept its old name everywhere else until the cache aged out.
 *
 * Not every key in the portal is here yet. Keys move in as the code that uses them is touched; the
 * rest are listed in {@link DOMAIN_ALIASES} so that invalidating a domain still reaches them.
 */

/** A brand filter: a brand id, or `undefined` for every brand the caller can see. */
export type BrandScope = string | undefined

/** The brand position in a key. Spelled out so `undefined` and a missing segment never collide. */
function scope(brand: BrandScope): string {
  return brand ?? 'all'
}

export const queryKeys = {
  brands: {
    /** `GET /api/brands`: the brands the caller can see. */
    list: () => ['brands'] as const,
    /** `GET /api/admin/brands`: every brand, enabled or not. */
    admin: () => ['brands', 'admin'] as const,
  },
  locations: {
    /** `GET /api/locations`. */
    list: () => ['locations'] as const,
    /** `GET /api/admin/locations`, optionally for one brand (`''` for all). */
    admin: (brand: string) => ['locations', 'admin', brand] as const,
  },
  deviceTypes: {
    list: () => ['device-types'] as const,
    admin: () => ['device-types', 'admin'] as const,
  },
  devices: {
    /** The device list's filters, whatever page asks for it. */
    list: (brand: BrandScope, filters: Record<string, unknown> = {}) =>
      ['devices', 'list', scope(brand), filters] as const,
    /** `GET /api/admin/devices`. */
    admin: (filters: Record<string, unknown>) => ['devices', 'admin', filters] as const,
    /**
     * `GET /api/fleet/stores`: a page of sites and the fleet's totals, for the dashboard and the
     * Sites page. Under `devices` so anything that changes devices refreshes it too.
     */
    sites: (brand: BrandScope, filters: Record<string, unknown> = {}) =>
      ['devices', 'sites', scope(brand), filters] as const,
    /** `GET /api/fleet/health`: the dashboard's health measures. */
    health: (brand: BrandScope) => ['devices', 'health', scope(brand)] as const,
    /** `GET /api/map/devices`: one pin per location, with its device counts. */
    map: (brand: BrandScope) => ['devices', 'map', scope(brand)] as const,
    /** A typeahead over the device list. */
    picker: (brand: BrandScope, search: string) =>
      ['devices', 'picker', scope(brand), search] as const,
    /**
     * The shared DevicePicker's server search. Its own segment, and the page size in the key, so
     * it never shares a cache entry with the member picker's 25-row search for the same text.
     */
    search: (brand: BrandScope, search: string, limit: number) =>
      ['devices', 'search', scope(brand), search, limit] as const,
    /**
     * One device. Still the legacy `['device', id]`, because the pages that read it are mid-edit
     * elsewhere; {@link DOMAIN_ALIASES} keeps it inside the `devices` domain until it moves.
     */
    detail: (id: string) => ['device', id] as const,
    /** `GET /api/admin/devices/{id}`: the administrative record the edit form reads. */
    adminDetail: (id: string) => ['admin-device', id] as const,
    /** What the device's agent says it is and can do. */
    capabilities: (id: string) => ['device-capabilities', id] as const,
    /** The groups a device belongs to. */
    groups: (id: string) => ['device-groups', id] as const,
  },
  dashboard: {
    /** `GET /api/stats`. */
    stats: (brand: BrandScope) => ['dashboard', 'stats', scope(brand)] as const,
  },
  groups: {
    /** `GET /api/groups`, for one brand or every brand the caller can see. */
    list: (brand: BrandScope) => ['groups', 'list', scope(brand)] as const,
    detail: (id: string) => ['group', id] as const,
  },
  jobs: {
    /** `GET /api/jobs`, optionally narrowed to one category (`''` for every category). */
    list: (category = '') => ['jobs', 'list', category] as const,
    detail: (id: string) => ['job', id] as const,
    /** The roles that may see and run a job. */
    roles: (id: string) => ['job-roles', id] as const,
    /** The events that start a job automatically. */
    triggers: (id: string) => ['job-triggers', id] as const,
    /** The managed variables library. */
    variables: () => ['job-variables'] as const,
  },
  /** A device's file system, as its agent reports it. */
  files: {
    /** One directory's listing. */
    list: (deviceId: string, path: string) => ['files', deviceId, path] as const,
    /** The disk usage under a path; null while none is asked for. */
    diskUsage: (deviceId: string, path: string | null) => ['disk-usage', deviceId, path] as const,
  },
  /** What a device's Docker daemon holds, as its agent reports it. */
  docker: {
    containers: (deviceId: string) => ['docker-containers', deviceId] as const,
    images: (deviceId: string) => ['docker-images', deviceId] as const,
    inspect: (deviceId: string, containerId: string) =>
      ['docker-inspect', deviceId, containerId] as const,
    logs: (deviceId: string, containerId: string) =>
      ['docker-logs', deviceId, containerId] as const,
  },
} as const

export type Domain = 'devices' | 'brands' | 'locations' | 'deviceTypes' | 'groups' | 'jobs'

/**
 * What else a change in each domain makes stale.
 *
 * The first entry is the domain's own prefix. The rest are either keys that have not moved under
 * it yet (`['device', id]`, `['admin-device-types']`) or other domains that *show* this one: a
 * device row carries its location and brand names, the map and the dashboard count devices, and
 * `/api/me` names the caller's brands.
 */
const DOMAIN_ALIASES: Record<Domain, QueryKey[]> = {
  devices: [['devices'], ['device'], ['dashboard'], ['group-device-search'], ['group-devices']],
  brands: [['brands'], ['devices'], ['device'], ['locations'], ['dashboard'], ['me']],
  locations: [['locations'], ['devices'], ['device'], ['dashboard']],
  deviceTypes: [['device-types'], ['admin-device-types'], ['devices'], ['device']],
  groups: [['groups'], ['group']],
  jobs: [['jobs'], ['job']],
}

/** The key prefixes a change in `domain` invalidates. Exported for tests. */
export function prefixesFor(domain: Domain): QueryKey[] {
  return DOMAIN_ALIASES[domain]
}

/**
 * Mark everything that shows `domain` stale, refetching what is on screen.
 *
 * Use this after a mutation rather than naming the one key the current page reads: the page that
 * made the change is rarely the only one showing the data.
 */
export async function invalidateDomain(
  queryClient: QueryClient,
  ...domains: Domain[]
): Promise<void> {
  const prefixes = new Map<string, QueryKey>()
  for (const domain of domains)
    for (const prefix of DOMAIN_ALIASES[domain]) prefixes.set(JSON.stringify(prefix), prefix)
  await Promise.all(
    [...prefixes.values()].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  )
}
